import { useState } from 'react';
import { useNavigate, useParams, useRouter } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Button, Card, ErrorState, FilterBar, PanelTitle, RangePicker, StatusPill } from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { defaultedSort } from '../../lib/table/clientSort';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { useSmallScreen } from '../../lib/util/smallScreen';
import { LeversPanel } from './LeversPanel';
import { TurnOnPricing } from './TurnOnPricing';
import { CITY_DEFAULT_SORT, CityFlags, CityRows, HealthMetrics, useHealthWindow } from './LocaleHealthScreen';
import { useLocaleLevers } from './leversQueries';
import { useLocaleHealth } from './queries';

/**
 * One locale — its own page, `/money/locale-health/<id>`, under Locale health
 * (no extra nav level; Back returns to the list with its filters). The money
 * cards for this locale over the range, its cities, and its levers in force.
 * While pricing is off (max price $0), Turn on pricing sets a max price.
 * Read-only on a phone: no Edit levers or Turn on pricing.
 */
export function LocalePage() {
  const { localeId } = useParams({ strict: false }) as { localeId: string };
  const router = useRouter();
  const navigate = useNavigate();
  const { locked } = useSession();
  const table = useTableUrlState();
  const w = useHealthWindow();
  const health = useLocaleHealth({ days: w.days, localeIds: [localeId] });
  const levers = useLocaleLevers(localeId);
  const [editing, setEditing] = useState(false);
  const small = useSmallScreen();

  const back = () =>
    router.history.canGoBack() ? router.history.back() : void navigate({ to: '/money/locale-health' as never });

  const rows = (health.data?.rows ?? []).filter((r) => r.localeId === localeId);
  const cities = rows.flatMap((r) => r.cities);
  const citySort = defaultedSort(table.sort, CITY_DEFAULT_SORT, table.setSort);
  const name = levers.data?.localeName ?? rows[0]?.localeName;
  const live = levers.data?.state.status === 'live';

  return (
    <div className="flex flex-col gap-[18px]">
      <Card surface="panel">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-[280px]">
            {levers.data && (
              <div className="mb-1.5">
                <StatusPill tone={live ? 'success' : 'neutral'} dot={live}>
                  {live ? 'Live' : 'Inactive'}
                </StatusPill>
              </div>
            )}
            <div className="font-heading text-[22px] font-bold tracking-[-.02em] text-brown-deep">
              {name ?? 'Locale'}
            </div>
            <div className="mt-[3px] text-[13px] text-muted">
              {cities.length} {cities.length === 1 ? 'city' : 'cities'} with orders · {w.label.toLowerCase()}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {levers.data && !small && !levers.data.state.paymentsEnabled && (
              <TurnOnPricing localeId={localeId} localeName={name ?? 'this locale'} levers={levers.data.levers} />
            )}
            {levers.data && !small && (
              <Button
                variant="ghost-dark"
                size="sm"
                lockedReason={locked('levers.write')}
                onClick={() => setEditing(true)}
              >
                Edit levers
              </Button>
            )}
            <Button size="sm" onClick={back}>
              <ArrowLeft size={14} aria-hidden="true" /> Back
            </Button>
          </div>
        </div>
      </Card>

      <FilterBar>
        <RangePicker value={w.range} onChange={w.setRange} />
        <span className="text-xs text-muted">Days are UTC.</span>
      </FilterBar>

      {health.isError && <ErrorState error={health.error} onRetry={() => void health.refetch()} compact />}

      <HealthMetrics rows={rows} rangeLabel={w.label} loading={health.isPending} failed={health.isError} />

      <CityFlags cities={cities} />

      <Card surface="panel">
        <PanelTitle eyebrow="Cities">Cities with orders</PanelTitle>
        <CityRows cities={cities} sort={citySort.sort} onSortChange={citySort.onSortChange} />
      </Card>

      <LeversPanel localeId={localeId} editing={editing} onEditingChange={setEditing} />
    </div>
  );
}
